import { useEffect, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { LayoutDashboard, Inbox, Menu, LogOut, FolderOpen, CreditCard, BarChart3, Building2, Tag, FileText } from 'lucide-react'
import { Shell, SidebarGroup, Topbar, Button } from '../ds/index'
import { Icon } from './Icon'
import { useSession } from '../lib/session'
import { useApi } from '../lib/useApi'
import { post } from '../lib/api'
import ViewAs, { ViewingBar } from './ViewAs'
import { BrandMark } from './BrandMark'
import { Moments } from './Moments'

// The same places on a phone (bottom bar, six) and a computer (sidebar, all).
// Billing covers the statement and invoices too: one place for money.
const NAV = [
  { to: '/portal', label: 'Overview', short: 'Home', icon: LayoutDashboard, match: (p: string) => p === '/portal' || p === '/portal/' },
  { to: '/portal/leads', label: 'Leads', short: 'Leads', icon: Inbox, match: (p: string) => /^\/portal\/(leads|disputes)/.test(p) },
  { to: '/portal/spending', label: 'Spending', short: 'Spending', icon: BarChart3, match: (p: string) => p.startsWith('/portal/spending') },
  { to: '/portal/billing', label: 'Billing', short: 'Billing', icon: CreditCard, match: (p: string) => /^\/portal\/(billing|statement|invoice)/.test(p) },
  // Prices is in the sidebar only: six tabs is what a phone's bottom bar holds. On a phone it opens from Billing.
  { to: '/portal/pricing', label: 'Prices', short: 'Prices', icon: Tag, match: (p: string) => p.startsWith('/portal/pricing'), phone: false },
  // The signed Terms (16.4): sidebar only, and linked from Profile on a phone.
  { to: '/portal/agreement', label: 'Agreement', short: 'Terms', icon: FileText, match: (p: string) => p.startsWith('/portal/agreement'), phone: false },
  { to: '/portal/resources', label: 'Resources', short: 'Help', icon: FolderOpen, match: (p: string) => p.startsWith('/portal/resources') },
  { to: '/portal/profile', label: 'Profile', short: 'Profile', icon: Building2, match: (p: string) => /^\/portal\/(profile|settings)/.test(p) },
]

/** The client company's portal: their leads, their balance, their settings. Nothing else. */
export default function ClientShell() {
  const [open, setOpen] = useState(false)
  const { me, refresh, config } = useSession()
  const location = useLocation()
  // Re-read on every page change so the "new leads" badge drops as soon as a lead is opened.
  const terms = useApi<{ update: { version: string; effective_at: string; in_effect: boolean } | null }>('/api/client/terms')
  const summary = useApi<{ client: { name: string }; unseen_leads: number; pauses?: { kind: string; reason: string }[] }>(`/api/client/summary?n=${encodeURIComponent(location.pathname)}`)
  const name = summary.data?.client.name || me?.org.name || 'Your leads'

  // The portal wears the install's brand (INSTALL_BRAND, read at serve time),
  // the same as the rest of the system, so nothing client-specific is compiled in.
  useEffect(() => { if (config?.brand) document.documentElement.dataset.brand = config.brand }, [config?.brand])

  const leave = async () => {
    await post('/api/auth/logout', {})
    await refresh()
  }

  return (
    <>
      {open && <div className="sidebar-backdrop only-mobile" onClick={() => setOpen(false)} role="presentation" />}
      <Shell
        sidebar={
          <nav className={open ? 'sidebar is-open' : 'sidebar'} aria-label="Main">
            <BrandMark fallbackName={name} sub={summary.data?.client.name} />
            <SidebarGroup label={me?.org.name ? `Leads from ${me.org.name}` : 'Your leads'} />
            {NAV.map((it) => (
              <Link key={it.to} to={it.to} className={it.match(location.pathname) ? 'nav-item active' : 'nav-item'} aria-current={it.match(location.pathname) ? 'page' : undefined} onClick={() => setOpen(false)}>
                <Icon of={it.icon} />
                <span>{it.label}</span>
                {it.to === '/portal/leads' && summary.data?.unseen_leads ? <span className="nav-badge" aria-label={`${summary.data.unseen_leads} new`}>{summary.data.unseen_leads}</span> : null}
              </Link>
            ))}
            <div className="powered" style={{ marginTop: 'auto', padding: 'var(--s-6) var(--s-5)' }}>Powered by SPEC Systems</div>
          </nav>
        }
        topbar={
          <Topbar
            left={<button className="btn btn-ghost only-mobile" onClick={() => setOpen(true)} aria-label="Open menu"><Icon of={Menu} /></button>}
            right={<>
              {me && <ViewAs me={me} />}
              <Button variant="ghost" onClick={leave} aria-label="Sign out"><Icon of={LogOut} /></Button>
            </>}
          />
        }
      >
        {me?.viewing_as && <ViewingBar me={me} />}
        {summary.data?.pauses?.length && !location.pathname.startsWith('/portal/billing') ? (
          <Link to="/portal/billing" className="paused-bar" role="status">
            <strong>New leads are paused.</strong> <span>{summary.data.pauses[0].reason}</span> <span className="paused-bar-go">See what to do</span>
          </Link>
        ) : null}
        {terms.data?.update && !location.pathname.startsWith('/portal/agreement') ? (
          <Link to="/portal/agreement" className={`terms-bar${terms.data.update.in_effect ? ' is-due' : ''}`} role="status">
            <strong>{terms.data.update.in_effect ? 'Please agree to the new Terms.' : 'New Terms are coming.'}</strong>{' '}
            <span>Version {terms.data.update.version} {terms.data.update.in_effect ? 'is in effect. Until you agree, the version you signed applies.' : `takes effect on ${new Date(terms.data.update.effective_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}.`}</span>
            <span className="paused-bar-go">{terms.data.update.in_effect ? 'Read and agree' : 'Read them'}</span>
          </Link>
        ) : null}
        <div className="portal-page"><Outlet /></div>
      </Shell>
      <Moments />
      <nav className="bottom-tabs only-mobile" aria-label="Sections">
        {NAV.filter((it) => it.phone !== false).map((it) => {
          const on = it.match(location.pathname)
          return (
            <Link key={it.to} to={it.to} className={on ? 'bottom-tab is-on' : 'bottom-tab'} aria-current={on ? 'page' : undefined}>
              <span className="bottom-tab-icon"><Icon of={it.icon} size={20} />
                {it.to === '/portal/leads' && summary.data?.unseen_leads ? <span className="bottom-tab-dot" aria-label={`${summary.data.unseen_leads} new`}>{summary.data.unseen_leads}</span> : null}
              </span>
              <span>{it.short}</span>
            </Link>
          )
        })}
      </nav>
    </>
  )
}
